/**
 * Which subdomains of the base domain a founder may have. Pure. A copy of ../api/src/domain/subdomains.ts, which is
 * the authority: this one only gives a friendly message as the admin types. A test in each repo pins both copies to
 * the same fixture (tests/fixtures/subdomains.json); change the two files and both fixtures together.
 */

/** Names we use or may use ourselves, matched exactly. */
export const RESERVED_SUBDOMAINS: readonly string[] = [
  'ops', 'admin', 'hq', 'api', 'app', 'auth', 'www', 'mail', 'email', 'smtp', 'relay', 'preview', 'previews', 'status',
  'help', 'support', 'billing', 'pay', 'payments', 'login', 'signin', 'signup', 'logout', 'account', 'accounts',
  'dashboard', 'console', 'demo', 'docs', 'blog', 'cdn', 'static', 'assets', 'stats', 'security', 'staff', 'team',
  'internal', 'system', 'root', 'dev', 'staging', 'prod', 'test', 'www1', 'www2', 'ns1', 'ns2', 'cpg', 'cpgcomputer',
  'listen', 'hci', 'matica', 'kinde', 'oauth', 'sso', 'id', 'my', 'me', 'home', 'about', 'legal', 'privacy', 'terms',
  'jobs', 'careers', 'press', 'news', 'store', 'shop', 'connect', 'setup', 'welcome', 'hello', 'mx', 'ftp', 'vpn', 'git',
]

/** Prefixes that would let an address pass for one of ours (e.g. login-fieldday). */
export const RESERVED_PREFIXES: readonly string[] = [
  'admin-', 'login-', 'signin-', 'support-', 'secure-', 'security-', 'account-', 'billing-', 'verify-', 'cpg-',
  'listen-', 'hci-', 'auth-', 'help-',
]

export const SUBDOMAIN_MIN = 4
export const SUBDOMAIN_MAX = 63

export const SUBDOMAIN_REASONS = {
  tooShort: 'too short',
  tooLong: 'too long',
  shape: 'use lowercase letters, digits and hyphens, with no hyphen at either end',
  reserved: 'reserved',
  ours: 'looks like one of our own addresses',
} as const

export type SubdomainCheck = { ok: true } | { ok: false; reason: string }

const LABEL = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/
const RESERVED = new Set(RESERVED_SUBDOMAINS)

export function checkSubdomain(name: string): SubdomainCheck {
  if (name.length < SUBDOMAIN_MIN) return { ok: false, reason: SUBDOMAIN_REASONS.tooShort }
  if (name.length > SUBDOMAIN_MAX) return { ok: false, reason: SUBDOMAIN_REASONS.tooLong }
  if (!LABEL.test(name)) return { ok: false, reason: SUBDOMAIN_REASONS.shape }
  if (RESERVED.has(name)) return { ok: false, reason: SUBDOMAIN_REASONS.reserved }
  if (RESERVED_PREFIXES.some((p) => name.startsWith(p))) return { ok: false, reason: SUBDOMAIN_REASONS.ours }
  return { ok: true }
}
